import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useServices } from '@/hooks/useServices';
import { useTranslation } from '@/features/i18n';
import { LanguageSwitcher } from '@/components/common';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: settings } = useSiteSettings();
  const { data: servicesData } = useServices();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const services = (servicesData || []).map((service) => ({
    label: service.title,
    href: `/services/${service.slug}`,
  }));

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = location.pathname.startsWith('/services');

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled ? 'py-3' : 'py-5'
      )}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-[60px]">
        <div className="flex items-center justify-between relative">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleHomeClick}
            className="flex items-center z-10"
          >
            <img
              src="/logo.png"
              alt={settings?.site_name || 'Din AI Bedrift'}
              className="h-18 w-auto"
            />
          </Link>

          {/* Desktop Navigation - NINITO PILL STYLE - Centered */}
          <div className="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2">
            <div className={cn(
              'flex items-center gap-1 px-4 py-2 rounded-full transition-all duration-300',
              'bg-white border border-black/5 shadow-[0_2px_20px_rgba(0,0,0,0.08)]'
            )}>
              <Link
                to="/"
                onClick={handleHomeClick}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-full transition-all',
                  isActive('/')
                    ? 'bg-black/5 text-black'
                    : 'text-black/60 hover:text-black hover:bg-black/5'
                )}
              >
                {t.nav.home}
              </Link>

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button
                  className={cn(
                    'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-all',
                    isServicesActive
                      ? 'bg-black/5 text-black'
                      : 'text-black/60 hover:text-black hover:bg-black/5'
                  )}
                >
                  {t.nav.services.title}
                  <ChevronDown className={cn(
                    'w-4 h-4 transition-transform',
                    isServicesOpen && 'rotate-180'
                  )} />
                </button>

                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-[20px] border border-black/5 py-2 shadow-xl"
                    >
                      <Link
                        to="/services"
                        className="flex items-center justify-between px-4 py-3 text-sm font-medium text-black hover:bg-black/5 transition-colors"
                      >
                        {t.nav.services.all}
                        <ArrowRight className="w-4 h-4 text-black/40" />
                      </Link>
                      <div className="my-1 mx-3 border-t border-black/10" />
                      {services.map((service) => (
                        <Link
                          key={service.href}
                          to={service.href}
                          className="block px-4 py-2.5 text-sm text-black/60 hover:bg-black/5 hover:text-black transition-colors"
                        >
                          {service.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/case-studies"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-full transition-all',
                  isActive('/case-studies')
                    ? 'bg-black/5 text-black'
                    : 'text-black/60 hover:text-black hover:bg-black/5'
                )}
              >
                {t.nav.caseStudies}
              </Link>

              <Link
                to="/blog"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-full transition-all',
                  isActive('/blog')
                    ? 'bg-black/5 text-black'
                    : 'text-black/60 hover:text-black hover:bg-black/5'
                )}
              >
                {t.nav.blog}
              </Link>

              <Link
                to="/about"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-full transition-all',
                  isActive('/about')
                    ? 'bg-black/5 text-black'
                    : 'text-black/60 hover:text-black hover:bg-black/5'
                )}
              >
                {t.nav.about}
              </Link>

              {/* CTA Button inside pill */}
              <Link
                to="/contact"
                className="flex items-center gap-2 ml-2 px-5 py-2.5 text-sm font-medium rounded-full bg-black text-white hover:bg-black/90 transition-all"
              >
                {t.nav.contact}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Right side - Language & Mobile menu */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2.5 rounded-xl transition-all text-black/70 hover:text-black hover:bg-black/5"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 top-16 bg-black/20 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white rounded-[20px] shadow-xl border border-black/5 overflow-hidden"
            >
              <div className="p-4 space-y-1">
                <div className="flex items-center justify-between px-4 py-3 mb-2 rounded-xl bg-black/5 sm:hidden">
                  <span className="text-sm font-medium text-black/60">Språk</span>
                  <LanguageSwitcher />
                </div>

                <Link
                  to="/"
                  onClick={(e) => { handleHomeClick(e); setIsOpen(false); }}
                  className={cn(
                    'block px-4 py-3 text-base font-medium rounded-xl transition-all',
                    isActive('/') ? 'bg-black/10 text-black' : 'text-black/70 hover:bg-black/5'
                  )}
                >
                  {t.nav.home}
                </Link>

                <Link
                  to="/services"
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block px-4 py-3 text-base font-medium rounded-xl transition-all',
                    isServicesActive ? 'bg-black/10 text-black' : 'text-black/70 hover:bg-black/5'
                  )}
                >
                  {t.nav.services.title}
                </Link>

                {services.length > 0 && (
                  <div className="pl-4 space-y-0.5">
                    {services.map((service) => (
                      <Link
                        key={service.href}
                        to={service.href}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 text-sm text-black/50 hover:text-black rounded-lg hover:bg-black/5"
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}

                <Link
                  to="/case-studies"
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block px-4 py-3 text-base font-medium rounded-xl transition-all',
                    isActive('/case-studies') ? 'bg-black/10 text-black' : 'text-black/70 hover:bg-black/5'
                  )}
                >
                  {t.nav.caseStudies}
                </Link>

                <Link
                  to="/blog"
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block px-4 py-3 text-base font-medium rounded-xl transition-all',
                    isActive('/blog') ? 'bg-black/10 text-black' : 'text-black/70 hover:bg-black/5'
                  )}
                >
                  {t.nav.blog}
                </Link>

                <Link
                  to="/about"
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block px-4 py-3 text-base font-medium rounded-xl transition-all',
                    isActive('/about') ? 'bg-black/10 text-black' : 'text-black/70 hover:bg-black/5'
                  )}
                >
                  {t.nav.about}
                </Link>

                <div className="pt-3">
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-black text-white font-medium rounded-full hover:bg-black/90 transition-all"
                  >
                    {t.nav.contact}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
